import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertsService } from "../../services/AlertsService";
import { getRefetchIntervalMs } from "../../utils/refreshInterval";

export const useOrganisationAlerts = (orgId, startDate, endDate) => {
  // Organisation alerts should use user refresh rate preference
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: ["organisationAlerts", orgId, startDate, endDate],
    queryFn: () =>
      AlertsService.fetchOrganisationAlerts(orgId, startDate, endDate),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: refetchIntervalMs,
  });
};

export const useDeviceAlerts = (deviceId, startDate, endDate) => {
  // Device alerts should use user refresh rate preference
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: ["deviceAlerts", deviceId, startDate, endDate],
    queryFn: () =>
      AlertsService.fetchDeviceAlerts(deviceId, startDate, endDate),
    enabled: !!deviceId,
    staleTime: 1000 * 60 * 2,
    refetchInterval: refetchIntervalMs,
  });
};

export const useAlert = (alertId) => {
  return useQuery({
    queryKey: ["alert", alertId],
    queryFn: () => AlertsService.fetchAlert(alertId),
    enabled: !!alertId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.createAlert,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      if (variables.deviceId) {
        queryClient.invalidateQueries({
          queryKey: ["deviceAlerts", variables.deviceId],
        });
      }
    },
  });
};

export const useUpdateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ alertId, data }) => AlertsService.updateAlert(alertId, data),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ["alert", alertId] });
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};

export const useDeleteAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.deleteAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};

export const useMarkAlertAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.markAsRead,
    onSuccess: (_, alertId) => {
      queryClient.invalidateQueries({ queryKey: ["alert", alertId] });
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};
